import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useInfiniteQuery, useQuery, keepPreviousData } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { api } from '@/lib/api';
import { pageTitle } from '@/lib/seo';
import { formatDistanceToNow, differenceInDays, differenceInHours, format } from 'date-fns';
import { VoteButton } from '@/components/VoteButton';
import { Header } from '@/components/Header';
import { CreatePostModal } from '@/components/CreatePostModal';
import { BoardsSidebar } from '@/components/BoardsSidebar';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { getUserDisplayName, getUserInitial, getUserAvatarUrl } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  activeStatuses,
  allStatuses,
  getSelectedStatuses,
} from '@/lib/postStatusFilters';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from '@/components/ui/drawer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Plus,
  MessageSquare,
  Loader2,
  Lightbulb,
  Flame,
  Sparkles,
  ChevronDown,
  LayoutList,
  List,
  SlidersHorizontal,
} from 'lucide-react';

interface Post {
  id: string;
  slug: string;
  title: string;
  details?: string;
  status: string;
  score: number;
  commentCount: number;
  created: string;
  hasVoted?: boolean;
  author: {
    id: string;
    name: string;
    alias?: string;
    avatarURL?: string;
    isMigrated?: boolean;
  };
  category?: {
    id: string;
    name: string;
    color?: string;
  };
}

interface Category {
  id: string;
  name: string;
  color?: string;
}

const statusConfig: Record<string, { label: string; variant: 'open' | 'review' | 'planned' | 'progress' | 'complete' | 'closed' | 'secondary' }> = {
  open: { label: 'Open', variant: 'open' },
  under_review: { label: 'Under Review', variant: 'review' },
  planned: { label: 'Planned', variant: 'planned' },
  in_progress: { label: 'In Progress', variant: 'progress' },
  completed: { label: 'Completed', variant: 'complete' },
  closed: { label: 'Closed', variant: 'closed' },
};



function PostCardSkeleton() {
  return (
    <div className="border rounded-xl p-5">
      <div className="flex gap-4">
        <Skeleton className="h-14 w-11 rounded-lg" />
        <div className="flex-1 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-5 w-20" />
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-5 w-5 rounded-full" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyStateIllustration() {
  return (
    <div className="relative w-32 h-32 mx-auto mb-6">
      {/* Background circles */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-32 h-32 rounded-full bg-primary/5" />
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-24 h-24 rounded-full bg-primary/10" />
      </div>
      {/* Icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center">
          <Lightbulb className="h-8 w-8 text-primary" />
        </div>
      </div>
      {/* Decorative sparkles */}
      <Sparkles className="absolute top-2 right-4 h-4 w-4 text-primary/40" />
      <Sparkles className="absolute bottom-4 left-2 h-3 w-3 text-primary/30" />
    </div>
  );
}

function formatPostDate(dateString: string): string {
  const date = new Date(dateString);
  const days = differenceInDays(new Date(), date);

  if (days <= 7) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  return format(date, 'MMM d, yyyy');
}

function isHotPost(post: Post): boolean {
  const hoursOld = differenceInHours(new Date(), new Date(post.created));
  // Hot if: high engagement relative to age
  // - Posted within 72 hours AND has 5+ votes
  // - OR posted within 24 hours AND has 3+ votes
  // - OR has high comment activity (5+ comments) within 48 hours
  if (hoursOld <= 24 && post.score >= 3) return true;
  if (hoursOld <= 72 && post.score >= 5) return true;
  if (hoursOld <= 48 && post.commentCount >= 5) return true;
  return false;
}

export default function BoardDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const isMobile = useIsMobile();
  const boardSlug = params.slug as string;
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);

  const [sortBy, setSortBy] = useState<'trending' | 'newest' | 'most-voted'>('trending');
  const [statusFilter, setStatusFilter] = useState<'active' | 'resolved' | 'all' | 'custom'>('active');
  const [customStatuses, setCustomStatuses] = useState<string[]>([...activeStatuses]);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'normal' | 'compact'>('normal');
  const [filtersLoaded, setFiltersLoaded] = useState(false);


  // Load filter preferences from localStorage on mount
  React.useEffect(() => {
    const savedSort = localStorage.getItem('board-sort') as 'trending' | 'newest' | 'most-voted';
    const savedStatus = localStorage.getItem('board-status') as 'active' | 'resolved' | 'all' | 'custom';
    const savedCustom = localStorage.getItem('board-custom-statuses');
    const savedViewMode = localStorage.getItem('board-view-mode') as 'normal' | 'compact';
    if (savedSort) setSortBy(savedSort);
    if (savedStatus) setStatusFilter(savedStatus);
    if (savedCustom) {
      try {
        setCustomStatuses(JSON.parse(savedCustom));
      } catch {
        // ignore parse errors
      }
    }
    if (savedViewMode) setViewMode(savedViewMode);
    setFiltersLoaded(true);
  }, []);

  // Save filter preferences to localStorage
  React.useEffect(() => {
    if (filtersLoaded) {
      localStorage.setItem('board-sort', sortBy);
    }
  }, [sortBy, filtersLoaded]);

  React.useEffect(() => {
    if (filtersLoaded) {
      localStorage.setItem('board-status', statusFilter);
    }
  }, [statusFilter, filtersLoaded]);

  React.useEffect(() => {
    if (filtersLoaded) {
      localStorage.setItem('board-custom-statuses', JSON.stringify(customStatuses));
    }
  }, [customStatuses, filtersLoaded]);

  React.useEffect(() => {
    if (filtersLoaded) {
      localStorage.setItem('board-view-mode', viewMode);
    }
  }, [viewMode, filtersLoaded]);

  const selectedStatuses = getSelectedStatuses(statusFilter, customStatuses);

  const toggleCustomStatus = (status: string) => {
    setCustomStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const { data: boardData, isLoading: boardLoading } = useQuery({
    queryKey: ['board', boardSlug],
    queryFn: () => api.boards.get(boardSlug),
    placeholderData: keepPreviousData,
  });

  // Get board ID from boardData for posts query
  const boardId = boardData?.board?.id;

  const {
    data: postsData,
    isFetchingNextPage,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['posts', boardId, sortBy, selectedStatuses, categoryFilter],
    queryFn: ({ pageParam = 1 }) => {
      const params: Record<string, unknown> = {
        boardId,
        page: pageParam,
        limit: 20,
        sort: sortBy,
      };
      if (selectedStatuses.length > 0) {
        params.status = selectedStatuses.join(',');
      }
      if (categoryFilter && categoryFilter !== 'all') {
        params.categoryId = categoryFilter;
      }
      return api.posts.list(params);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.page < lastPage.pagination.pages) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!boardId,
    // Keep showing previous data while fetching new filter results
    placeholderData: keepPreviousData,
  });

  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [target] = entries;
    if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  React.useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0,
      rootMargin: '100px',
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);

  const handleCreateClick = () => {
    if (isAuthenticated) {
      setIsCreateModalOpen(true);
    } else {
      navigate(`/auth/login?redirect=/boards/${boardSlug}`);
    }
  };

  const board = boardData?.board;
  const categories: Category[] = board?.categories || [];
  const posts: Post[] = postsData?.pages.flatMap(page => page.posts) || [];
  const totalPosts = postsData?.pages[postsData.pages.length - 1]?.pagination?.total || 0;

  // Track if we've ever loaded data to avoid showing skeleton on board switches
  const hasEverLoadedRef = useRef(false);
  useEffect(() => {
    if (board) {
      hasEverLoadedRef.current = true;
    }
  }, [board]);

  // Only show skeleton on very first load, never after we've seen any data
  // This prevents skeleton flash when switching between boards
  const showSkeleton = !board && boardLoading && !hasEverLoadedRef.current;

  // Don't show empty state while fetching - prevents border flash during filter changes
  const showEmptyState = posts.length === 0 && !isFetching;

  if (showSkeleton) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>{pageTitle('Loading board…')}</title>
        </Helmet>
        <Header />
        <div className="border-b">
          <div className="h-1.5 bg-gradient-to-r from-primary/30 to-primary/10" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Skeleton className="h-10 w-64 mb-3" />
            <Skeleton className="h-5 w-96 mb-3" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-8">
            {/* Sidebar skeleton - hidden on mobile */}
            <div className="w-[220px] shrink-0 hidden lg:block pt-6">
              <Skeleton className="h-5 w-20 mb-3 ml-3" />
              <div className="space-y-1">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="px-3 py-2 flex items-center gap-2 mx-2">
                    <Skeleton className="h-4 w-4" />
                    <Skeleton className="h-4 flex-1" />
                    <Skeleton className="h-4 w-6" />
                  </div>
                ))}
              </div>
            </div>
            {/* Main content skeleton */}
            <div className="flex-1 min-w-0">
              <div className="border-b bg-background/40 backdrop-blur-md sticky top-[52px] z-40 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex gap-4">
                  <Skeleton className="h-10 flex-1" />
                  <Skeleton className="h-10 w-32" />
                  <Skeleton className="h-10 w-32" />
                </div>
              </div>
              <div className="py-6 space-y-3">
                {[...Array(5)].map((_, i) => (
                  <PostCardSkeleton key={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>{pageTitle('Board not found')}</title>
        </Helmet>
        <Header />
        <div className="flex items-center justify-center py-24">
          <Card className="text-center p-8">
            <CardContent>
              <p className="text-xl font-semibold mb-4">Board not found</p>
              <Button asChild variant="outline">
                <Link to="/boards">Back to Boards</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageTitle(board.name)}</title>
      </Helmet>
      <Header />

      {/* Board Header */}
      <div className="border-b">
        <div className="h-1.5 bg-gradient-to-r from-primary to-primary/60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-3xl font-bold tracking-tight">{board.name}</h1>
              {board.description && (
                <p className="text-muted-foreground mt-2 text-lg">{board.description}</p>
              )}
              <p className="text-sm text-muted-foreground mt-3">
                {totalPosts} {totalPosts === 1 ? 'idea' : 'ideas'}
              </p>
            </div>
            {/* Desktop Create Button */}
            <Button
              onClick={handleCreateClick}
              size="lg"
              className="hidden sm:flex"
            >
              <Lightbulb className="h-4 w-4 mr-2" />
              Share Idea
            </Button>
          </div>
        </div>
      </div>

      {/* Main content area with sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <BoardsSidebar />

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Filters */}
            <div className="border-b bg-background/40 backdrop-blur-md sticky top-[52px] z-40 -mx-4 sm:-mx-6 lg:mx-0 px-4 sm:px-6 lg:px-0 py-4">
              {/* Mobile Filters Button */}
              {isMobile ? (
                <div className="flex items-center justify-between gap-2">
                  {/* Sort - Compact on mobile */}
                  <div className="flex items-center gap-1 p-1 bg-muted rounded-lg" role="radiogroup" aria-label="Sort posts by">
                    {[
                      { value: 'trending', label: 'Trending' },
                      { value: 'newest', label: 'New' },
                      { value: 'most-voted', label: 'Top' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        role="radio"
                        aria-checked={sortBy === option.value}
                        onClick={() => setSortBy(option.value as typeof sortBy)}
                        className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          sortBy === option.value
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {/* Filters Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMobileFiltersOpen(true)}
                    className="gap-2"
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    Filters
                    {(statusFilter !== 'active' || categoryFilter !== 'all') && (
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                        {(statusFilter !== 'active' ? 1 : 0) + (categoryFilter !== 'all' ? 1 : 0)}
                      </Badge>
                    )}
                  </Button>
                </div>
              ) : (
                /* Desktop Filters */
                <div className="flex flex-wrap gap-2 justify-end">
                  {/* Category Filter */}
                  {categories.length > 0 && (
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger className="w-[160px] bg-background">
                        <SelectValue placeholder="Category" className="truncate" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <span className="flex items-center gap-2">
                              <span
                                className="w-2 h-2 rounded-full shrink-0"
                                style={{ backgroundColor: cat.color || '#6B7280' }}
                              />
                              <span className="truncate">{cat.name}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Sort - Segmented Control */}
                  <div className="flex items-center gap-1 p-1 bg-muted rounded-lg" role="radiogroup" aria-label="Sort posts by">
                    {[
                      { value: 'trending', label: 'Trending' },
                      { value: 'newest', label: 'New' },
                      { value: 'most-voted', label: 'Top' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        role="radio"
                        aria-checked={sortBy === option.value}
                        onClick={() => setSortBy(option.value as typeof sortBy)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                          sortBy === option.value
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {/* Status Filter */}
                  <div className="flex items-center gap-1 p-1 bg-muted rounded-lg" role="radiogroup" aria-label="Filter posts by status">
                    {[
                      { value: 'active', label: 'Active' },
                      { value: 'resolved', label: 'Resolved' },
                      { value: 'all', label: 'All' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        role="radio"
                        aria-checked={statusFilter === option.value}
                        onClick={() => setStatusFilter(option.value as typeof statusFilter)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                          statusFilter === option.value
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                    {/* Custom Status Filter */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${
                            statusFilter === 'custom'
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          Custom
                          {statusFilter === 'custom' && customStatuses.length > 0 && (
                            <span className="text-xs text-muted-foreground">({customStatuses.length})</span>
                          )}
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuLabel>Select Statuses</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {allStatuses.map((status) => (
                          <DropdownMenuCheckboxItem
                            key={status}
                            checked={customStatuses.includes(status)}
                            onCheckedChange={() => {
                              toggleCustomStatus(status);
                              setStatusFilter('custom');
                            }}
                            onSelect={(e) => e.preventDefault()}
                          >
                            {statusConfig[status]?.label || status}
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* View Mode Toggle */}
                  <div className="flex items-center gap-1 p-1 bg-muted rounded-lg" role="radiogroup" aria-label="View mode">
                    <button
                      role="radio"
                      aria-checked={viewMode === 'normal'}
                      aria-label="Normal view"
                      onClick={() => setViewMode('normal')}
                      className={`p-1.5 rounded-md transition-colors ${
                        viewMode === 'normal'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      title="Normal view"
                    >
                      <LayoutList className="h-4 w-4" />
                    </button>
                    <button
                      role="radio"
                      aria-checked={viewMode === 'compact'}
                      aria-label="Compact view"
                      onClick={() => setViewMode('compact')}
                      className={`p-1.5 rounded-md transition-colors ${
                        viewMode === 'compact'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      title="Compact view"
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Filters Drawer */}
            <Drawer open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <DrawerContent side="bottom">
                <DrawerHeader>
                  <DrawerTitle>Filters</DrawerTitle>
                </DrawerHeader>
                <div className="p-4 space-y-6">
                  {/* Category Filter */}
                  {categories.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Category</label>
                      <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Categories</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <span className="flex items-center gap-2">
                                <span
                                  className="w-2 h-2 rounded-full shrink-0"
                                  style={{ backgroundColor: cat.color || '#6B7280' }}
                                />
                                <span>{cat.name}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Status Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Status</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: 'active', label: 'Active' },
                        { value: 'resolved', label: 'Resolved' },
                        { value: 'all', label: 'All' },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setStatusFilter(option.value as typeof statusFilter)}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                            statusFilter === option.value
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Statuses */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Custom Statuses</label>
                    <div className="flex flex-wrap gap-2">
                      {allStatuses.map((status) => (
                        <button
                          key={status}
                          onClick={() => {
                            toggleCustomStatus(status);
                            setStatusFilter('custom');
                          }}
                          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            statusFilter === 'custom' && customStatuses.includes(status)
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {statusConfig[status]?.label || status}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* View Mode */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">View Mode</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setViewMode('normal')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                          viewMode === 'normal'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <LayoutList className="h-4 w-4" />
                        Normal
                      </button>
                      <button
                        onClick={() => setViewMode('compact')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                          viewMode === 'compact'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <List className="h-4 w-4" />
                        Compact
                      </button>
                    </div>
                  </div>
                </div>
                <DrawerFooter>
                  <Button onClick={() => setMobileFiltersOpen(false)}>
                    Apply Filters
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setStatusFilter('active');
                      setCategoryFilter('all');
                      setViewMode('normal');
                    }}
                  >
                    Reset
                  </Button>
                </DrawerFooter>
              </DrawerContent>
            </Drawer>

            {/* Posts List */}
            <main className="py-6 pb-24 sm:pb-8">
        {showEmptyState ? (
          <Card className="text-center py-16 border-dashed">
            <CardContent>
              {totalPosts === 0 ? (
                <>
                  <EmptyStateIllustration />
                  <p className="text-xl font-semibold">No ideas yet</p>
                  <p className="text-muted-foreground mt-2 max-w-sm mx-auto">
                    Be the first to share your thoughts and help shape the future of this product.
                  </p>
                  <Button className="mt-6" size="lg" onClick={handleCreateClick}>
                    <Lightbulb className="h-4 w-4 mr-2" />
                    Share Your Idea
                  </Button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-lg font-semibold">No posts match your filters</p>
                  <p className="text-muted-foreground mt-1">Try selecting different filters</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => {
                      setStatusFilter('all');
                      setCategoryFilter('all');
                    }}
                  >
                    Reset Filters
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className={viewMode === 'compact' ? '' : 'space-y-3'}>
            {posts.map((post) => {
              const status = statusConfig[post.status] || statusConfig.open;
              const hot = isHotPost(post);

              if (viewMode === 'compact') {
                return (
                  <Link
                    key={post.id}
                    to={`/posts/${post.slug}`}
                    className="block"
                  >
                    <div className="flex items-center gap-2 px-2 py-1.5 border-b border-border hover:bg-muted/50 transition-colors">
                      <div className="w-10 shrink-0 flex justify-end">
                        <VoteButton
                          postId={post.id}
                          initialScore={post.score}
                          initialVoted={post.hasVoted || false}
                          size="sm"
                          disabled={user?.id === post.author.id}
                          hideIcon
                        />
                      </div>
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <h3 className="text-sm font-medium truncate">
                          {post.title}
                        </h3>
                        {hot && (
                          <Flame className="h-3 w-3 text-orange-500 shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {post.commentCount}
                        </span>
                        <Badge variant={status.variant} className="text-[10px] px-1.5 py-0">
                          {status.label}
                        </Badge>
                      </div>
                    </div>
                  </Link>
                );
              }

              return (
                <Link
                  key={post.id}
                  to={`/posts/${post.slug}`}
                  className="block"
                >
                  <Card className="hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden">
                    <CardContent className="p-5">
                      <div className="flex gap-4">
                        <div className="flex-shrink-0">
                          <VoteButton
                            postId={post.id}
                            initialScore={post.score}
                            initialVoted={post.hasVoted || false}
                            size="md"
                            disabled={user?.id === post.author.id}
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3 mb-1">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-base font-semibold leading-tight">
                                  {post.title}
                                </h3>
                                {hot && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-600 text-xs font-medium">
                                    <Flame className="h-3 w-3" />
                                    Hot
                                  </span>
                                )}
                              </div>
                            </div>
                            <Badge variant={status.variant} className="shrink-0 text-xs">
                              {status.label}
                            </Badge>
                          </div>

                          {post.details && (
                            <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                              {post.details.replace(/<[^>]*>/g, '')}
                            </p>
                          )}

                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                            {/* Author with avatar */}
                            <span className="flex items-center gap-1.5">
                              <Avatar className="h-5 w-5">
                                <AvatarImage src={getUserAvatarUrl(post.author) || undefined} />
                                <AvatarFallback className="text-[10px]" colorSeed={post.author.id}>
                                  {getUserInitial(post.author)}
                                </AvatarFallback>
                              </Avatar>
                              <span>{getUserDisplayName(post.author)}</span>
                            </span>

                            <span className="text-muted-foreground/50">·</span>

                            {/* Comments */}
                            <span className="flex items-center gap-1">
                              <MessageSquare className="h-3.5 w-3.5" />
                              {post.commentCount}
                            </span>

                            {/* Category */}
                            {post.category && (
                              <>
                                <span className="text-muted-foreground/50">·</span>
                                <Badge
                                  variant="secondary"
                                  className="text-xs px-2 py-0"
                                  style={{
                                    backgroundColor: `${post.category.color}15`,
                                    color: post.category.color || undefined
                                  }}
                                >
                                  {post.category.name}
                                </Badge>
                              </>
                            )}

                            <span className="text-muted-foreground/50">·</span>

                            {/* Date */}
                            <span>{formatPostDate(post.created)}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}

            {isFetchingNextPage && (
              <div className="py-8 flex justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            <div ref={observerTarget} className="h-4" />
          </div>
        )}
      </main>
          </div>
        </div>
      </div>

      {/* Mobile FAB */}
      <div className="fixed bottom-6 right-6 sm:hidden z-50">
        <Button
          onClick={handleCreateClick}
          size="lg"
          className="h-14 w-14 rounded-full shadow-lg"
        >
          <Plus className="h-6 w-6" />
        </Button>
      </div>

      <CreatePostModal
        boardId={boardId}
        boardName={board.name}
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        categories={board.categories}
      />
    </div>
  );
}
